import { Component, HostListener, OnDestroy } from '@angular/core';
import { CXoneAcdClient, CXoneVoiceContact } from '@nice-devone/acd-sdk';
import { AgentStateEvent, CXonePopUrl } from '@nice-devone/common-sdk';
import {
  CXoneDigitalClient,
  CXoneDigitalContact,
} from '@nice-devone/digital-sdk';
import { LoggerService } from 'src/app/core/services/logger.service';
import { LoginService } from 'src/app/core/services/login.service';
import { SdkAcdAgent } from 'src/app/data/CXOne/sdk-acd/sdk-acd.service';
import { NiceSdkAuthenticationService } from 'src/app/data/CXOne/sdk-auth/nice-sdk-authentication.service';
import { SdkDigitalService } from 'src/app/data/CXOne/sdk-digital/sdk-digital.service';
import { CxOneUtils } from 'src/app/data/CXOne/util/CxOneUtils';
import { DynamicsNpcService } from 'src/app/data/Dynamics/npc/dynamics-npc.service';
import { EventHandlerService } from 'src/app/data/services/event-handler.service';
import { VoiceContactStatusAgent } from 'src/app/shared/enums/voice-contact-status-agent.enum';
import { CIFDatadogService } from 'src/app/shared/services/cif-datadog';
import {
  FocusEventDTO,
  FocusEventSource,
} from '../pessoa-fisica/domain/ContactCenter/FocusEventDTO';
import { environment } from 'src/environments/environment';
import { AdminService } from '@nice-devone/core-sdk';
import { ContactCenterMainService } from 'src/app/data/CXOne/services/main/contact-center-main.service';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import { MediaType } from 'src/app/utils/media-type';
import { getMediaType } from 'src/app/utils/cxone-utils';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';
import { SessionStorageUtils } from 'src/app/data/CXOne/util/SessionStorageUtils';

const tableName = 'cb_interacao';

@Component({
  selector: 'app-npc',
  standalone: true,
  imports: [],
  templateUrl: './npc.component.html',
  styleUrl: './npc.component.css',
})
export class NpcComponent implements OnDestroy {
  private isInAutenticadora = false;
  private pendingPopUrls = new Map<string, CXonePopUrl>();
  private uraReturnTimer: any = null;
  private uraContactIds = new Set<string>();
  private pendingCloseTimers = new Map<string, any>();
  private readonly URA_RETURN_TIMEOUT_MS = 8000;
  private readonly URA_TRANSFER_GRACE_MS = 5000;

  constructor(
    private loginService: LoginService,
    private logger: LoggerService,
    private niceSdkAuthService: NiceSdkAuthenticationService,
    private eventHandler: EventHandlerService,
    private sdkAcdAgent: SdkAcdAgent,
    private sdkDigital: SdkDigitalService,
    private dynamics: DynamicsNpcService,
    private mainService: ContactCenterMainService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
    private jsonConverterUtils: JsonConverterUtils,
    private sessionStorageUtils: SessionStorageUtils,
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    this.logger.warn(`NPC - Fechando/Atualizando aba do navegador!`);
    event.preventDefault();
    event.returnValue = '';
  }

  async ngOnInit() {
    this.logger.info('NPC - inicializado');
    const centralId = this.localStorageUtils.safeGetItem('centralId') || 'npc';
    const authServiceType =
      this.localStorageUtils.safeGetItem('authServiceType') || 'nice';
    const authMode =
      this.localStorageUtils.safeGetItem('display_mode') || 'popup';

    try {
      const isAuthenticated = await this.loginService.login(
        authServiceType,
        centralId,
        true,
        authMode,
      );
      if (isAuthenticated) {
        this.logger.info('NPC - Usuario autenticado');
        await this.niceSdkAuthService.launchAgent();

        await this.sdkAcdAgent.initialize();
        await this.sdkDigital.initializeEvents();
        this.sdkAcdAgent.instance.screenPop.screenPopSubscribe(
          true,
          true,
          true,
          true,
          true,
          true,
        );

        try {
          this.logger.info('NPC - Inicializando dynamics.');
          await this.dynamics.initialize(environment.cif_versao2_npc);

          const input = {
            templateName: 'cb_nice',
            templateParameters: {},
            context: {
              sessaobetta: true,
            },
          };

          await this.dynamics.cifCreateSession(input, null, '');
        } catch (error) {
          this.logger.error('NPC - Erro ao inicializar Dynamics: ', error);
        }

        CXoneAcdClient.instance.agentStateService.agentStateSubject.subscribe(
          async (agentState: AgentStateEvent) => {
            this.logger.info('NPC (ACD) - Agent State: ', agentState);
            let agentNextState = agentState.nextState.reason;

            if (agentNextState === 'Autenticadora') {
              this.isInAutenticadora = true;
              this.cancelarContingenciaUra();
              this.marcarContatoEmAutenticadora();
              const sessionId = this.localStorageUtils.safeGetItem(
                'sessionBeingAttended',
              );

              this.cifDatadogService.ura.leaveToUra({
                masterId: sessionId ?? 'npc-ura',
                mediaType: 'voice',
              });

              try {
                await (window as any).Microsoft.CIFramework.raiseEvent(
                  'envio_ura_autenticadora',
                  {
                    sessionId: sessionId,
                  },
                );

                this.logger.info(
                  'NPC - Enviando evento de URA Autenticadora para o Dynamics',
                );
              } catch (error) {
                this.logger.error(
                  'NPC - Erro ao enviar evento de URA Autenticadora para o Dynamics: ',
                  error,
                  'sessionId:',
                  sessionId,
                );
              }

              return;
            }

            if (
              agentState.nextState.state === 'Available' &&
              this.isInAutenticadora
            ) {
              this.armarContingenciaUra();
            }
          },
        );

        CXoneAcdClient.instance.contactManager.voiceContactUpdateEvent.subscribe(
          async (voiceContact: CXoneVoiceContact) => {
            const status = voiceContact.status || '';
            const contactId = voiceContact.contactID || '';
            const contatoEvento = { id: contactId, status: status };
            let eventoProcessado = this.eventHandler.isProcessed(contatoEvento);

            this.logger.info(
              `NPC (ACD) - Voice Contact status: ${status} | evento: `,
              voiceContact,
            );

            const attachdataValues = this.sdkAcdAgent.deserializeScreenPopUrl(
              voiceContact.screenPopUrl,
            );

            if (
              status === VoiceContactStatusAgent.ACTIVE &&
              attachdataValues['r_ura'] === 'S'
            ) {
              await this.liberarTelaRetornoUra(
                attachdataValues['u_json'],
                'evento_voz',
              );
            }

            if (
              status === VoiceContactStatusAgent.ACTIVE &&
              !eventoProcessado
            ) {
              this.eventHandler.add(contatoEvento);
              await this.processVoiceContactEvent(voiceContact);
            }

            if (status === VoiceContactStatusAgent.DISCONNECTED) {
              this.agendarEncerramentoSessao(voiceContact, contatoEvento);
            }
          },
        );

        CXoneDigitalClient.instance.digitalContactManager.onDigitalContactEvent.subscribe(
          async (digitalContact: CXoneDigitalContact) => {
            this.logger.info(
              `NPC - NICE Event Details: ${JSON.stringify(digitalContact.eventDetails)}`,
            );

            if (digitalContact.eventDetails.eventType === 'CaseInboxAssigned') {
              this.logger.info(
                'NPC - evento de digital recebido: ',
                JSON.stringify(digitalContact.case),
              );

              this.DigitalContactCaseAssignedHandler(digitalContact);
            }
            if (
              digitalContact.eventDetails.eventType === 'CaseInboxUnassigned'
            ) {
              this.DigitalContactCaseUnassignedHandler(digitalContact);
            }
          },
        );

        this.sdkAcdAgent.instance.screenPop.popUrlSubject.subscribe(
          (popUrlData: CXonePopUrl) => {
            this.logger.info(
              'NPC - screen pop url event',
              JSON.stringify(popUrlData),
            );
            this.processPopUrlEvent(popUrlData);
          },
        );
      }
    } catch (error) {
      this.logger.error('NPC - Erro ao autenticar usuario: ', error);
    }
  }

  ngOnDestroy(): void {
    this.cancelarContingenciaUra();
    this.pendingCloseTimers.forEach((timer) => clearTimeout(timer));
    this.pendingCloseTimers.clear();
  }

  private contatoEmAtendimento(): string | null {
    const sessionId = this.localStorageUtils.safeGetItem(
      'sessionBeingAttended',
    );

    if (!sessionId) {
      return null;
    }

    return this.localStorageUtils.safeGetItem(sessionId);
  }

  private marcarContatoEmAutenticadora(): void {
    const contactId = this.contatoEmAtendimento();

    if (!contactId) {
      this.logger.warn(
        'NPC - Não foi possível identificar o contato transferido para a URA Autenticadora',
      );
      return;
    }

    this.uraContactIds.add(contactId);
    this.cancelarEncerramentoPendente(contactId);

    this.logger.info(
      'NPC - Contato marcado como em ciclo de URA Autenticadora, encerramento de sessão bloqueado: ',
      contactId,
    );
  }

  private cancelarEncerramentoPendente(contactId: string): void {
    const timer = this.pendingCloseTimers.get(contactId);

    if (!timer) {
      return;
    }

    clearTimeout(timer);
    this.pendingCloseTimers.delete(contactId);

    this.logger.info(
      'NPC - Encerramento de sessão cancelado para o contato: ',
      contactId,
    );
  }

  private agendarEncerramentoSessao(
    voiceContact: CXoneVoiceContact,
    contatoEvento: { id: string; status: string },
  ): void {
    const contactId = voiceContact.contactID || '';

    if (this.emCicloDeUra(contactId)) {
      this.logger.info(
        'NPC - Contato em ciclo de URA Autenticadora, sessão não será encerrada: ',
        contactId,
      );
      return;
    }

    this.cancelarEncerramentoPendente(contactId);

    const timer = setTimeout(() => {
      this.pendingCloseTimers.delete(contactId);
      this.encerrarSessao(voiceContact, contatoEvento);
    }, this.URA_TRANSFER_GRACE_MS);

    this.pendingCloseTimers.set(contactId, timer);
  }

  private emCicloDeUra(contactId: string): boolean {
    return this.isInAutenticadora || this.uraContactIds.has(contactId);
  }

  private async encerrarSessao(
    voiceContact: CXoneVoiceContact,
    contatoEvento: { id: string; status: string },
  ): Promise<void> {
    const contactId = voiceContact.contactID || '';

    if (this.emCicloDeUra(contactId)) {
      this.logger.info(
        'NPC - Ciclo de URA Autenticadora detectado na janela de graça, sessão não será encerrada: ',
        contactId,
      );
      return;
    }

    this.cancelarContingenciaUra();
    this.eventHandler.remove(contatoEvento);

    const sessionId = this.localStorageUtils.safeGetItem(contactId);

    if (!sessionId) {
      this.logger.warn(
        'NPC - sessionId não encontrado no localStorage, não é possível encerrar',
        contactId,
      );
      return;
    }

    await this.fecharSessaoNoDynamics(contactId, sessionId, 'evento de voz');
  }

  private async encerrarSessaoPorContingencia(): Promise<void> {
    const sessionId = this.localStorageUtils.safeGetItem(
      'sessionBeingAttended',
    );
    const contactId = this.contatoEmAtendimento() ?? '';

    this.uraContactIds.clear();

    if (!sessionId) {
      this.logger.warn(
        'NPC - Contingência: sessionBeingAttended não encontrado, nenhuma sessão a encerrar',
      );
      return;
    }

    this.cancelarEncerramentoPendente(contactId);
    this.eventHandler.remove({
      id: contactId,
      status: VoiceContactStatusAgent.DISCONNECTED,
    });

    await this.fecharSessaoNoDynamics(contactId, sessionId, 'contingência');
  }

  private async fecharSessaoNoDynamics(
    contactId: string,
    sessionId: string,
    origem: string,
  ): Promise<void> {
    try {
      await this.dynamics.cifCloseSession(
        sessionId,
        sessionId,
        contactId,
        null,
      );

      this.logger.info(
        `NPC - sessão encerrada com sucesso (${origem})`,
        sessionId,
      );

      this.localStorageUtils.safeRemoveItem(contactId);
      this.localStorageUtils.safeRemoveItem(sessionId);
      this.localStorageUtils.safeRemoveItem('sessionBeingAttended');
    } catch (error) {
      this.logger.error(
        `NPC - Erro ao encerrar sessão (${origem}): `,
        error,
        'sessionId:',
        sessionId,
      );
    }
  }

  private async liberarTelaRetornoUra(
    attachRetornoUra?: any,
    motivo: 'evento_voz' | 'contingencia' = 'evento_voz',
  ): Promise<void> {
    if (!this.isInAutenticadora) {
      this.logger.info(
        `NPC - Retorno de URA Autenticadora já processado, ignorando (motivo: ${motivo})`,
      );
      return;
    }

    this.isInAutenticadora = false;
    this.cancelarContingenciaUra();

    if (motivo === 'evento_voz') {
      this.uraContactIds.clear();
    }

    const sessionId = this.localStorageUtils.safeGetItem(
      'sessionBeingAttended',
    );

    this.logger.info(
      `NPC - Liberando tela do agente, enviando evento de retorno de URA Autenticadora para o Dynamics. Motivo: ${motivo}`,
      'sessionId:',
      sessionId,
    );

    try {
      if (motivo === 'contingencia') {
        await (window as any).Microsoft.CIFramework.raiseEvent(
          'retorno_ura_autenticadora',
        );

        this.cifDatadogService.ura.cancel({
          masterId: sessionId ?? 'npc-ura',
          mediaType: 'voice' as MediaType,
        });

        await this.encerrarSessaoPorContingencia();
        return;
      }

      await (window as any).Microsoft.CIFramework.raiseEvent(
        'retorno_ura_autenticadora',
        {
          sessionId: sessionId,
          attachRetornoUra: attachRetornoUra,
        },
      );

      if (motivo === 'evento_voz') {
        this.cifDatadogService.ura.returnToUra({
          masterId: sessionId ?? 'npc-ura',
          mediaType: 'voice',
          attachData: attachRetornoUra,
        });
      }
    } catch (error) {
      this.logger.error(
        'NPC - Erro ao enviar evento de retorno de URA Autenticadora para o Dynamics: ',
        error,
        'sessionId:',
        sessionId,
      );

      this.cifDatadogService.ura.fail({
        masterId: sessionId ?? 'npc-ura',
        mediaType: 'voice',
        attachData: null,
        error: error,
      });
    }
  }

  private armarContingenciaUra(): void {
    this.cancelarContingenciaUra();

    this.logger.info(
      `NPC - Agente voltou para estado Available, armando contingência de ${this.URA_RETURN_TIMEOUT_MS}ms aguardando evento de voz do retorno da URA Autenticadora. A contingência libera apenas a tela, sem encerrar a sessão.`,
    );

    this.uraReturnTimer = setTimeout(
      () => this.liberarTelaRetornoUra(undefined, 'contingencia'),
      this.URA_RETURN_TIMEOUT_MS,
    );
  }

  private cancelarContingenciaUra(): void {
    if (this.uraReturnTimer) {
      clearTimeout(this.uraReturnTimer);
      this.uraReturnTimer = null;
      this.logger.info('NPC - Contingência de retorno de URA cancelada');
    }
  }

  private async processVoiceContactEvent(
    voiceContact: CXoneVoiceContact,
  ): Promise<void> {
    try {
      const attachdataValues = this.sdkAcdAgent.deserializeScreenPopUrl(
        voiceContact.screenPopUrl,
      );

      this.logger.info('NPC - ScreenPopUrl deserializado: ', attachdataValues);

      this.logger.info('NPC - attachdataValues: ', attachdataValues);

      const dataDynamics = this.transformAttachdataToDataDynamics(
        attachdataValues,
        voiceContact.contactID,
      );
      const attachDataObj: any = CxOneUtils.parseSingleQuotedJson(
        attachdataValues.u_json,
      );

      this.logger.info('NPC - Data send to Dynamics: ', dataDynamics);

      if (!voiceContact.isInbound && !attachdataValues.callback) {
        this.logger.info(
          `NPC (ACD - Contato não é inbound, não executando a criação de ticket no dynamics`,
        );
        return;
      }

      const result = await this.dynamics.cifCreateTicket(
        tableName,
        dataDynamics,
        voiceContact.contactID,
        'voice',
        attachdataValues.u_json,
      );

      if (result && result.id_interacao && !result.id_ticket) {
        result.id_ticket = await this.dynamics.cifCreateBlankTicket(
          result.id_interacao,
        );
      }

      if (!result || !result.id_interacao || !result.id_ticket) {
        this.logger.error('NPC - Erro ao criar ticket: ', result);
        return;
      }

      this.logger.info(
        `NPC - Ticket criado com sucesso: ID Interação: ${result.id_interacao}, ID Ticket: ${result.id_ticket}`,
      );

      if (!attachDataObj?.['U_SESSION_ID_AQ5']) {
        this.logger.info(
          `NPC - Enviando dados para script de inserção de dados, contato sem sessionId AQ5`,
        );
        AdminService.instance.signalScript(voiceContact.contactID, {
          p1: 'inserirDados',
          p2: `idTicket:${result.id_ticket}`,
        });
      }

      const input_sessao_atendimento = {
        templateName: 'cb_nice_sessao',
        templateParameters: {
          id_interacao: result.id_interacao,
          id_ticket: result.id_ticket,
          customerName: attachDataObj?.['U_NOME_CLIENTE'],
          customerRecordId: attachDataObj?.['U_CLIENT_ID'],
          customerEntityName: 'contact',
          cb_cliente_on: true, // CONTROLE DE ESTADO
          conn_id: '',
        },
        context: {
          sessaobetta: false,
        },
      };

      this.logger.info(
        `NPC - Criando sessão de atendimento`,
        input_sessao_atendimento,
      );

      // abre uma aba com o ticket que foi aberto e salva o sessionId no localStorage
      const sessionId = await this.dynamics.cifCreateSession(
        input_sessao_atendimento,
        'voice',
        voiceContact.contactID,
      );
      this.localStorageUtils.safeSetItem(voiceContact.contactID, sessionId);
      this.localStorageUtils.safeSetItem(sessionId, voiceContact.contactID);
      this.localStorageUtils.safeSetItem('sessionBeingAttended', sessionId);
    } catch (error) {
      this.logger.error(
        'NPC - Erro ao processar evento de contato de voz: ',
        error,
      );
    }
  }

  private transformAttachdataToDataDynamics(
    attachdataValues: any,
    connid: string,
  ): any {
    return {
      cb_id_externo: connid,
      cb_pl_tipo: '1', // VOZ (PADRÃO)
      cb_mst_attachdata: attachdataValues?.['u_json'] || null,
      statuscode: 121430000,
    };
  }

  private sanitizeStringValue(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return String(value);
  }

  private async DigitalContactCaseAssignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let attachdata: any = {};
    let channel = '';
    let caseId = '';
    let popUrl: any = '';
    let isPopUrl = false;

    if (!digitalContact) {
      this.logger.error('NPC - Evento principal digitalContact não recebido');
      throw new Error('DIGITAL_CONTACT_NULL');
    }

    //Recebendo o valor do caseId
    caseId = digitalContact.caseId;
    this.logger.info('NPC - ContactId do evento digital recebido: ', caseId);

    const ujson = await this.mainService.generatePopUrlFronDigitalContact(
      digitalContact,
      'NPC',
    );

    if (!ujson) {
      this.logger.warn('NPC - Dados não obtidos da extração do digitalEvent.');
      isPopUrl = true;
    } else {
      popUrl = ujson.popUrl;
      channel = ujson.channel;
    }

    if (isPopUrl === true) {
      popUrl = await this.handleFallbackPopUrl(digitalContact.caseId);

      const attachData = CxOneUtils.CreateDictionaryFromPopUrl(popUrl) ?? {};

      attachdata = attachData.U_JSON;
    } else {
      const jsonData = JSON.stringify(popUrl);
      const popUrlData = this.jsonConverterUtils.JsonParsed(jsonData);
      attachdata = JSON.stringify(popUrlData.u_json);
    }

    this.logger.info(
      'NPC - Iniciando a criação do ticket, ',
      JSON.stringify(popUrl),
    );

    this.createDigitalTicket(
      tableName,
      attachdata,
      digitalContact.caseId,
      getMediaType(digitalContact),
    );
  }

  private DigitalContactCaseUnassignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    this.logger.info('unassigned event', digitalContact);

    const sessionIdToBeClosed = this.localStorageUtils.safeGetItem(
      digitalContact.caseId,
    );

    this.sessionStorageUtils.safeSetSessionItem(
      `sessionToBeClosed_${digitalContact.caseId}`,
      sessionIdToBeClosed ?? '',
    );

    this.dynamics.cifCloseSessionDigital(
      digitalContact.caseId,
      getMediaType(digitalContact),
    );

    return;
  }

  private async createDigitalTicket(
    tableName: string,
    attachData: any,
    connId: string,
    mediaType: MediaType,
  ) {
    //this.logger.info('Iniciando a criação do ticket', attachData);

    let dataDynamics;
    try {
      dataDynamics = this.transformeAttachDataDigitalToDynamics(
        attachData,
        connId,
        mediaType,
      );
    } catch (error) {
      this.logger.error(
        'NPC - Erro ao parsear attachData: ',
        error,
        'attachData:',
        attachData,
      );
      dataDynamics = null;
      return;
    }

    this.logger.info('NPC - attachDataDynamics', JSON.stringify(dataDynamics));
    const result = await this.dynamics.cifCreateTicket(
      tableName,
      dataDynamics,
      connId,
      mediaType,
      attachData.U_JSON,
    );

    if (result && result.id_interacao && !result.id_ticket) {
      result.id_ticket = await this.dynamics.cifCreateBlankTicket(
        result.id_interacao,
      );
    }

    if (!result || !result.id_interacao || !result.id_ticket) {
      this.logger.error('NPC - Erro ao criar ticket: ', result);
      return;
    }

    this.logger.info(
      `NPC - Ticket criado com sucesso: ID Interação: ${result.id_interacao}, ID Ticket: ${result.id_ticket}`,
    );

    var forceFocused = await this.ForceFocused();

    const input_sessao_atendimento = {
      templateName: 'cb_nice_sessao',
      templateParameters: {
        id_interacao: result.id_interacao,
        id_ticket: result.id_ticket,
        customerName: attachData?.['CLIENTNAME'],
        customerRecordId: attachData?.['u_client_id'],
        customerEntityName: 'contact',
        cb_cliente_on: true, // CONTROLE DE ESTADO,
        conn_id: connId,
      },
      isFocused: forceFocused,
      context: {
        sessaobetta: false,
      },
    };

    this.logger.info(
      `NPC - Criando sessão de atendimento`,
      JSON.stringify(input_sessao_atendimento),
    );

    // abre uma aba com o ticket que foi aberto e salva o sessionId no localStorage
    const sessionId = await this.dynamics.cifCreateSession(
      input_sessao_atendimento,
      mediaType,
      connId,
    );
    this.localStorageUtils.safeSetItem(connId, sessionId);
    this.localStorageUtils.safeSetItem(sessionId, connId);
    this.localStorageUtils.safeSetItem('sessionBeingAttended', sessionId);
  }

  private async ForceFocused(): Promise<boolean> {
    if (await this.IsDefaultDynamicsSessionFocused()) {
      return true;
    }

    return false;
  }

  private async IsDefaultDynamicsSessionFocused(): Promise<boolean> {
    try {
      var dynamicsFocused =
        (await (window as any).Microsoft.CIFramework.getFocusedSession()) ??
        'notFocused';
    } catch (e) {
      return false;
    }

    if (
      dynamicsFocused === 'session-id-0' ||
      dynamicsFocused === 'session-id-1'
    ) {
      return true;
    }

    return false;
  }

  private transformeAttachDataDigitalToDynamics(
    attachData: any,
    connId: string,
    channelType: string,
  ) {
    if (channelType == 'whatsapp') {
      return {
        cb_id_externo: connId,
        cb_mst_attachdata: attachData,
        cb_pl_tipo: attachData?.cb_pl_tipo ?? '4',
        statuscode: 121430000,
      };
    }
    return {
      cb_id_externo: connId,
      cb_mst_attachdata: attachData,
      statuscode: 121430000,
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async processPopUrlEvent(popUrlData: CXonePopUrl) {
    if (popUrlData.tabTitle?.toUpperCase() === 'ATTACHESDYNAMICS') {
      this.pendingPopUrls.set(popUrlData.contactId, popUrlData);
      this.logger.info(
        'Attachdata amazenado para contato digital: ',
        popUrlData.contactId,
      );
    }

    if (popUrlData.tabTitle?.toUpperCase() === 'FOCUS') {
      const focusEvent: FocusEventDTO = {
        Id: popUrlData.contactId,
        Source: FocusEventSource.ContactCenter,
      };
      this.localStorageUtils.safeSetItem('interactionFocused', focusEvent.Id);
      this.logger.info('NPC - Focus test', JSON.stringify(focusEvent));
      this.dynamics.ChangeSessionFocus(popUrlData.contactId);
    }
  }

  private async handleFallbackPopUrl(caseId: string) {
    let popUrl = '';

    this.logger.info('NPC - Iniciando busca pelo evento de popUrl');

    for (let tentativa = 1; tentativa <= 10; tentativa++) {
      this.logger.info(`NPC - Tentativa ${tentativa} de 10.`);

      popUrl = this.pendingPopUrls.get(caseId)?.url ?? '';

      if (popUrl != '') {
        this.logger.info('Pop url com attach encontrado: ', caseId);
        break;
      }

      this.logger.warn(
        'PopUrl com attach não encontrado para o contato digital: ',
        caseId,
      );

      await CxOneUtils.Delay(1000);
    }

    return popUrl;
  }
}
